import { getActivePreset } from '../components/DateRangePicker';

export const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export const compactCurrency = (value) => {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(2)}K`;
  return currencyFormatter.format(value);
};

export const compactCurrencyAxis = (value) => {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${Math.round(value / 1_000)}K`;
  return `$${value}`;
};

export const changePercent = (current, prior) => {
  if (prior === 0 || prior == null) return null;
  return ((current - prior) / Math.abs(prior) * 100).toFixed(1);
};

export const formatYMD = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const getComparisonRange = (dateRange) => {
  const preset = getActivePreset(dateRange);
  if (!dateRange.start || !dateRange.end) return { start: null, end: null, label: '' };

  const start = new Date(dateRange.start + 'T00:00:00');
  const end = new Date(dateRange.end + 'T00:00:00');

  const shiftDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
  const shiftMonth = (d, n) => {
    const r = new Date(d);
    const targetMonth = r.getMonth() + n;
    r.setDate(1);
    r.setMonth(targetMonth);
    const lastDay = new Date(r.getFullYear(), r.getMonth() + 1, 0).getDate();
    r.setDate(Math.min(d.getDate(), lastDay));
    return r;
  };
  const shiftYear = (d, n) => { const r = new Date(d); r.setFullYear(r.getFullYear() + n); return r; };
  const monthsBefore = (months) => ({
    start: formatYMD(new Date(start.getFullYear(), start.getMonth() - months, 1)),
    end: formatYMD(shiftDays(start, -1)),
  });

  switch (preset) {
    case 'today':
      return { start: formatYMD(shiftDays(start, -1)), end: formatYMD(shiftDays(end, -1)), label: 'vs yesterday' };
    case 'yesterday':
      return { start: formatYMD(shiftDays(start, -1)), end: formatYMD(shiftDays(end, -1)), label: 'vs day before' };
    case 'this_week':
      return { start: formatYMD(shiftDays(start, -7)), end: formatYMD(shiftDays(end, -7)), label: 'vs last week' };
    case 'last_week':
      return { start: formatYMD(shiftDays(start, -7)), end: formatYMD(shiftDays(end, -7)), label: 'vs prior week' };
    case 'this_month':
      return { ...monthsBefore(1), label: 'vs last month' };
    case 'last_month':
      return { ...monthsBefore(1), label: 'vs prior month' };
    case 'month_to_date':
      return { start: formatYMD(shiftMonth(start, -1)), end: formatYMD(shiftMonth(end, -1)), label: 'vs last month to date' };
    case 'year_to_date':
      return { start: formatYMD(shiftYear(start, -1)), end: formatYMD(shiftYear(end, -1)), label: 'vs last year to date' };
    case 'this_year':
      return { start: formatYMD(shiftYear(start, -1)), end: formatYMD(shiftYear(end, -1)), label: 'vs last year' };
    case 'last_year':
      return { start: formatYMD(shiftYear(start, -1)), end: formatYMD(shiftYear(end, -1)), label: 'vs prior year' };
    default: {
      const endsMonth = shiftDays(end, 1).getDate() === 1;
      if (start.getDate() === 1 && endsMonth) {
        const months = (end.getFullYear() - start.getFullYear()) * 12 + end.getMonth() - start.getMonth() + 1;
        return { ...monthsBefore(months), label: months === 1 ? 'vs prior month' : 'vs prior period' };
      }
      const diffMs = end - start;
      const priorEnd = new Date(start.getTime() - 86400000);
      const priorStart = new Date(priorEnd.getTime() - diffMs);
      return { start: formatYMD(priorStart), end: formatYMD(priorEnd), label: 'vs prior period' };
    }
  }
};

export const getGranularity = (dateRange) => {
  const preset = getActivePreset(dateRange);

  if (preset === 'today' || preset === 'yesterday') return 'day';
  if (preset === 'this_week' || preset === 'last_week') return 'day';
  if (preset === 'this_month' || preset === 'last_month' || preset === 'month_to_date') return 'month';
  if (preset === 'this_year' || preset === 'last_year' || preset === 'year_to_date') return 'month';

  if (dateRange.start && dateRange.end) {
    const s = new Date(dateRange.start + 'T00:00:00');
    const e = new Date(dateRange.end + 'T00:00:00');
    const diffDays = Math.round((e - s) / (1000 * 60 * 60 * 24));
    if (diffDays <= 31) return 'day';
    if (diffDays <= 120) return 'week';
    return 'month';
  }

  return 'month';
};

export const getSalesChartGranularity = (dateRange) => {
  if (!dateRange?.start || !dateRange?.end) return 'month';
  const diffDays = Math.round((new Date(`${dateRange.end}T00:00:00`) - new Date(`${dateRange.start}T00:00:00`)) / 86400000);
  if (diffDays <= 31) return 'day';
  if (diffDays <= 112) return 'week';
  return 'month';
};

export const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
};

export const revenueBy = (rows, column, topN = 7) => {
  const totals = rows.reduce((acc, row) => {
    const label = row[column] || '(none)';
    acc[label] = (acc[label] || 0) + (row.revenue || 0);
    return acc;
  }, {});
  const sorted = Object.entries(totals)
    .map(([name, revenue]) => ({ name, revenue }))
    .filter((entry) => entry.revenue > 0)
    .sort((a, b) => b.revenue - a.revenue);
  if (sorted.length <= topN + 1) return sorted;

  const other = sorted.slice(topN).reduce((sum, entry) => sum + entry.revenue, 0);
  return [...sorted.slice(0, topN), { name: 'Other', revenue: other }];
};

export const formatVenueDateTime = (dateStr, timeZone) => {
  if (!dateStr) return '\u2014';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    ...(timeZone ? { timeZone } : {}),
  });
};
